import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook agnóstico de payment + transport methods + telemóveis MBWay.
 *
 * As callsites injectam adapters para os seus endpoints. O CRM e o MY usam
 * shapes ligeiramente diferentes na lista de payment methods (CRM tem `Id`,
 * MY tem `ID_TipoPagamento`) — normalizamos via `methodIdField`.
 *
 * @param {object} cfg
 * @param {() => Promise<Array>} cfg.fetchPaymentMethods
 * @param {() => Promise<Array>} cfg.fetchTransportMethods
 * @param {() => Promise<Array>} [cfg.fetchPhoneNumbers]
 * @param {(phone:string) => Promise<{ok, message?}>} [cfg.addPhoneNumber]
 * @param {string} [cfg.methodIdField] default `Id` (CRM); MY usa `ID_TipoPagamento`.
 * @param {string} [cfg.naId] valor a filtrar do select (default `'NA'`).
 */
export default function usePaymentMethodsLogic({
  fetchPaymentMethods, fetchTransportMethods,
  fetchPhoneNumbers, addPhoneNumber,
  methodIdField = 'Id',
  naId = 'NA',
} = {}) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transportMethods, setTransportMethods] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const [selectedPayment, setSelectedPayment] = useState('');
  const [selectedTransport, setSelectedTransport] = useState('');
  const [mbwayPhone, setMbwayPhone] = useState('');
  const [parcelas, setParcelas] = useState(1);

  const reloadPhones = useCallback(async () => {
    if (typeof fetchPhoneNumbers !== 'function') return;
    try { setPhoneNumbers((await fetchPhoneNumbers()) || []); }
    catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('reloadPhones failed', e);
    }
  }, [fetchPhoneNumbers]);

  useEffect(() => {
    if (typeof fetchPaymentMethods === 'function') {
      fetchPaymentMethods().then((list) => setPaymentMethods(list || [])).catch(() => {});
    }
    if (typeof fetchTransportMethods === 'function') {
      fetchTransportMethods().then((list) => setTransportMethods(list || [])).catch(() => {});
    }
    reloadPhones();
  }, [fetchPaymentMethods, fetchTransportMethods, reloadPhones]);

  const addPhone = useCallback(async (phone) => {
    const trimmed = (phone || '').trim();
    if (!trimmed || typeof addPhoneNumber !== 'function') return { ok: false };
    const r = await addPhoneNumber(trimmed);
    if (r?.ok) {
      await reloadPhones();
      setMbwayPhone(trimmed);
    }
    return r || { ok: false };
  }, [addPhoneNumber, reloadPhones]);

  const currentPayment = useMemo(
    () => paymentMethods.find((m) => (m[methodIdField] ?? m.Id ?? m.ID_TipoPagamento) === selectedPayment),
    [paymentMethods, methodIdField, selectedPayment],
  );
  const isPhonePayment = currentPayment?.PagamentoPorTelemovel === true;
  const isInstallments = currentPayment?.EmParcelas === true;
  const maxParcelas = currentPayment?.NMaxParcelas || 0;

  const selectablePaymentMethods = useMemo(
    () => paymentMethods.filter((m) => (m[methodIdField] ?? m.Id ?? m.ID_TipoPagamento) !== naId),
    [paymentMethods, methodIdField, naId],
  );

  return {
    paymentMethods, transportMethods, phoneNumbers,
    selectedPayment, setSelectedPayment,
    selectedTransport, setSelectedTransport,
    mbwayPhone, setMbwayPhone,
    parcelas, setParcelas,
    addPhone,
    currentPayment, isPhonePayment, isInstallments, maxParcelas,
    selectablePaymentMethods,
  };
}
